import React from 'react';
import { VisitType, VisitPriority } from '../../types/rental';

interface VisitPrintDocumentProps {
  visit: any;
  contractMachines?: any[];
}

export const VisitPrintDocument: React.FC<VisitPrintDocumentProps> = ({ visit, contractMachines }) => {
  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 15mm;
        }

        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          margin: 0;
          padding: 0;
          color: #000;
          background: white;
        }

        .print-document {
          max-width: 210mm;
          margin: 0 auto;
          background: white;
          padding: 0;
        }

        .header-section {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          padding: 25px;
          margin-bottom: 25px;
          border-radius: 8px;
        }

        .header-section h1 {
          margin: 0 0 8px 0;
          font-size: 32px;
          font-weight: 700;
          letter-spacing: 1px;
        }

        .header-section .visit-number {
          font-size: 18px;
          opacity: 0.95;
          margin: 0;
        }

        .section-title {
          background: #f3f4f6;
          border-left: 5px solid #3b82f6;
          padding: 12px 15px;
          margin: 20px 0 15px 0;
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }

        .info-item {
          border: 1px solid #e5e7eb;
          padding: 12px;
          border-radius: 6px;
          background: #fafafa;
        }

        .info-label {
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          font-weight: 600;
        }

        .info-value {
          font-size: 15px;
          color: #111827;
          font-weight: 600;
        }

        .info-value.urgent {
          color: #dc2626;
        }

        .table-container {
          margin: 15px 0;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          overflow: hidden;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        thead {
          background: #e5e7eb;
        }

        thead th {
          padding: 12px;
          text-align: left;
          font-size: 12px;
          font-weight: 700;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #9ca3af;
        }

        tbody td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          font-size: 13px;
          color: #1f2937;
        }

        tbody tr:last-child td {
          border-bottom: none;
        }

        tbody tr:hover {
          background: #f9fafb;
        }

        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .badge-selected {
          background: #d1fae5;
          color: #065f46;
        }

        .badge-not-selected {
          background: #f3f4f6;
          color: #4b5563;
        }

        .notes-box {
          border: 1px solid #d1d5db;
          padding: 15px;
          min-height: 80px;
          background: #fafafa;
          border-radius: 6px;
          font-size: 13px;
          line-height: 1.6;
        }

        .notes-box.empty {
          color: #9ca3af;
          font-style: italic;
        }

        .checklist {
          background: white;
          padding: 15px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
        }

        .checklist-item {
          display: flex;
          align-items: center;
          padding: 10px;
          border-bottom: 1px solid #f3f4f6;
        }

        .checklist-item:last-child {
          border-bottom: none;
        }

        .checklist-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin-right: 12px;
          cursor: pointer;
        }

        .checklist-item label {
          font-size: 14px;
          color: #374151;
          cursor: pointer;
        }

        .signature-section {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid #d1d5db;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 40px;
        }

        .signature-box {
          border: 1px solid #d1d5db;
          padding: 20px;
          border-radius: 6px;
          background: #fafafa;
        }

        .signature-box .title {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          font-weight: 600;
        }

        .signature-line {
          border-bottom: 2px solid #000;
          height: 60px;
          margin-bottom: 15px;
        }

        .signature-info {
          font-size: 12px;
          color: #4b5563;
          margin: 6px 0;
        }

        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 11px;
          color: #9ca3af;
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-document {
            padding: 0;
          }

          .header-section {
            break-inside: avoid;
          }

          .section-title {
            break-after: avoid;
          }

          .table-container {
            break-inside: avoid;
          }

          .signature-section {
            break-inside: avoid;
          }
        }
      `}</style>

      <div className="print-document">
        {/* Header */}
        <div className="header-section">
          <h1>SERVICE VISIT WORK ORDER</h1>
          <p className="visit-number">Visit #: {visit.visitNumber}</p>
        </div>

        {/* Visit Details */}
        <div className="section-title">Visit Information</div>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">Visit Type</div>
            <div className="info-value">{visit.visitType?.replace(/_/g, ' ')}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Priority</div>
            <div className={`info-value ${visit.priority === VisitPriority.URGENT ? 'urgent' : ''}`}>
              {visit.priority} {visit.priority === VisitPriority.URGENT && '⚠️'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">Scheduled Date</div>
            <div className="info-value">
              {new Date(visit.visitDate).toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">Status</div>
            <div className="info-value">{visit.visitStatus?.replace(/_/g, ' ')}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Contract Number</div>
            <div className="info-value">{visit.contract?.contractNumber}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Assigned Technician</div>
            <div className="info-value">{visit.employee?.name || 'Not assigned'}</div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="section-title">Customer Information</div>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">Customer Name</div>
            <div className="info-value">{visit.customer?.name}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Phone Number</div>
            <div className="info-value">{visit.customer?.phone || 'N/A'}</div>
          </div>
          <div className="info-item" style={{gridColumn: 'span 2'}}>
            <div className="info-label">Address</div>
            <div className="info-value">{visit.customer?.address || 'N/A'}</div>
          </div>
          {visit.customer?.email && (
            <div className="info-item" style={{gridColumn: 'span 2'}}>
              <div className="info-label">Email</div>
              <div className="info-value">{visit.customer.email}</div>
            </div>
          )}
        </div>

        {/* Machines to Service */}
        <div className="section-title">Equipment to Service</div>
        {visit.machines && visit.machines.length > 0 ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{width: '60px'}}>#</th>
                  <th>Serial Number</th>
                  <th>Product Model</th>
                  <th style={{width: '120px'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visit.machines.map((machine: any, index: number) => (
                  <tr key={machine.id}>
                    <td>{index + 1}</td>
                    <td><strong>{machine.serialNumber}</strong></td>
                    <td>{machine.productName}</td>
                    <td>
                      <span className="badge badge-selected">✓ Selected</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>No machines selected for this visit</p>
        )}

        {/* Other Machines */}
        {contractMachines && contractMachines.length > 0 && (
          <>
            <div className="section-title">Other Equipment at Location (Not Scheduled)</div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{width: '60px'}}>#</th>
                    <th>Serial Number</th>
                    <th>Product Model</th>
                    <th style={{width: '120px'}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contractMachines.map((machine: any, index: number) => (
                    <tr key={machine.id}>
                      <td>{index + 1}</td>
                      <td>{machine.serialNumber?.serialNumber}</td>
                      <td>{machine.serialNumber?.product?.name}</td>
                      <td>
                        <span className="badge badge-not-selected">Not Selected</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Scent Change Info */}
        {visit.visitType === VisitType.SCENT_CHANGE && (
          <>
            <div className="section-title">Scent Change Details</div>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Current Scent</div>
                <div className="info-value">{visit.fromScent || 'N/A'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">New Scent Requested</div>
                <div className="info-value">{visit.toScent || 'N/A'}</div>
              </div>
            </div>
          </>
        )}

        {/* Special Instructions */}
        <div className="section-title">Special Instructions / Notes</div>
        <div className={`notes-box ${!visit.requestNotes ? 'empty' : ''}`}>
          {visit.requestNotes || 'No special instructions provided'}
        </div>

        {/* Work Checklist */}
        <div className="section-title">Service Checklist</div>
        <div className="checklist">
          <div className="checklist-item">
            <input type="checkbox" id="check1" />
            <label htmlFor="check1">Equipment inspection completed</label>
          </div>
          <div className="checklist-item">
            <input type="checkbox" id="check2" />
            <label htmlFor="check2">Maintenance performed as required</label>
          </div>
          {visit.visitType === VisitType.REFILL && (
            <div className="checklist-item">
              <input type="checkbox" id="check3" />
              <label htmlFor="check3">Oil/fluid refill completed</label>
            </div>
          )}
          {visit.visitType === VisitType.SCENT_CHANGE && (
            <div className="checklist-item">
              <input type="checkbox" id="check4" />
              <label htmlFor="check4">Scent change completed successfully</label>
            </div>
          )}
          <div className="checklist-item">
            <input type="checkbox" id="check5" />
            <label htmlFor="check5">Equipment tested and operational</label>
          </div>
          <div className="checklist-item">
            <input type="checkbox" id="check6" />
            <label htmlFor="check6">Area cleaned and organized</label>
          </div>
          <div className="checklist-item">
            <input type="checkbox" id="check7" />
            <label htmlFor="check7">Customer briefed on service performed</label>
          </div>
        </div>

        {/* Completion Notes */}
        <div className="section-title">Completion Notes (To be filled by technician)</div>
        <div className="notes-box">
          {visit.completionNotes || <span style={{height: '80px', display: 'block'}}>&nbsp;</span>}
        </div>

        {/* Signatures */}
        <div className="signature-section">
          <div className="signature-box">
            <div className="title">Technician Signature</div>
            <div className="signature-line"></div>
            <div className="signature-info">Name: {visit.employee?.name || '________________________'}</div>
            <div className="signature-info">Date: ________________________</div>
            <div className="signature-info">Time: ________________________</div>
          </div>
          <div className="signature-box">
            <div className="title">Customer Signature</div>
            <div className="signature-line"></div>
            <div className="signature-info">Name: ________________________</div>
            <div className="signature-info">Date: ________________________</div>
            <div className="signature-info">Time: ________________________</div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer">
          <p>Document generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
          <p>This document serves as an official service record</p>
        </div>
      </div>
    </>
  );
};
